/**
 * Kit brinde no add-to-cart — modal no lugar da sacola.
 *
 * Fluxo: o cliente adiciona um produto-gatilho → em vez da sacola abre uma modal
 * com as peças do kit (um select de cor por peça) → ele adiciona o kit OU recusa
 * (e aí passa por uma confirmação) → só então a sacola abre.
 *
 * COMO A SACOLA É SEGURADA (e por que o guard global não serve)
 * Na 3.x bastava `window.__consoSilentCartUpdate`: o `conso-cart-drawer-guard.js`
 * embrulhava o `showDialog` do `cart-drawer-component`. Na 4.x quem abre a sacola
 * é o `<theme-drawer>` (`cart-drawer.js` faz `this.closest('theme-drawer').open()`)
 * e o `cart-drawer-component` nem herda mais de `DialogComponent` — aquele guard
 * virou no-op. Aqui a guarda é local: embrulha `ThemeDrawer.prototype.open` e só
 * ignora a abertura do drawer QUE CONTÉM a sacola, e só na janela entre o clique
 * no add-to-cart e a modal aparecer. Mesma abordagem do `conso-lp-brinde.js`.
 *
 * PREÇO
 * O tema não zera nada. As duas linhas entram pelo preço cheio com a property
 * `_conso_brinde_lp`; quem desconta é a function `compre-ganhe` do app, com
 * `exigirMarcador: true`. Como desconto de function só materializa no checkout,
 * a sacola mostra o valor cheio — o setting `nota` da section existe pra avisar.
 *
 * O KIT É TUDO OU NADA
 * Uma única `/cart/add.js` com as duas linhas. Se uma peça falhar (variante que
 * esgotou entre a carga da página e o clique), a chamada inteira falha e nada
 * entra — meio kit no carrinho renderia a function sem par e o cliente pagaria
 * a peça solta.
 */
(() => {
  if (window.__consoBrindeKitAtc) return;
  window.__consoBrindeKitAtc = true;

  const SEL_DADOS = 'script[type="application/json"][data-conso-brinde-kit]';
  // Os dois lugares de onde sai um add real. Um submit qualquer (busca,
  // newsletter) não pode armar a modal.
  const SEL_FORM = 'product-form-component[data-product-id], sticky-add-to-cart[data-product-id]';
  const SEL_ATC = 'button[type="submit"], .add-to-cart-button, .sticky-add-to-cart__button';
  const EVENTO_ADD = 'shopify:cart:lines-update';
  const PROP_BRINDE = '_conso_brinde_lp';
  const MARCA_GUARDA = '__consoBrindeKitGuarded';
  // Se o add falhar sem disparar evento nenhum, a guarda ficaria ligada e a
  // sacola não abriria mais na página. Este é o teto.
  const TETO_ESPERA_MS = 8000;

  const rotas = () => (window.Theme && window.Theme.routes) || {};

  let dados = null;
  let pendente = false;
  let expiracao = null;
  let segurando = false;
  let modal = null;
  let confirma = null;
  let aceitou = false;
  let via = 'esc';
  /** Cor escolhida por peça, na ordem das peças. */
  let escolhas = [];

  /* ------------------------------------------------------- guarda do drawer */

  function armarGuarda() {
    const ThemeDrawer = window.customElements && customElements.get('theme-drawer');
    if (!ThemeDrawer || ThemeDrawer.prototype[MARCA_GUARDA]) return;
    ThemeDrawer.prototype[MARCA_GUARDA] = true;

    const original = ThemeDrawer.prototype.open;
    if (typeof original !== 'function') return;

    ThemeDrawer.prototype.open = function () {
      // Duas janelas, nao uma. `segurando` cobre o intervalo entre o clique no
      // add-to-cart e a modal aparecer. `modal.open` cobre o resto: o
      // cart-drawer da 4.x tenta abrir DUAS vezes — uma sincrona no evento e
      // outra no `.then` da promise do add. Essa segunda resolve quando a
      // resposta do carrinho chega, e no mobile isso e depois da modal ja estar
      // na tela. Sem a checagem do `modal.open` a sacola subia por cima dela.
      const nossaModalNaTela = Boolean(modal && modal.open);
      if ((segurando || nossaModalNaTela) && this.querySelector('cart-drawer-component')) return undefined;
      return original.apply(this, arguments);
    };
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('theme-drawer').then(armarGuarda);
  }

  function liberar() {
    segurando = false;
  }

  function desarmar() {
    pendente = false;
    clearTimeout(expiracao);
    liberar();
  }

  /* ------------------------------------------------------------------ dados */

  function lerDados() {
    if (dados !== null) return dados || null;

    const el = document.querySelector(SEL_DADOS);
    if (!el) {
      dados = false;
      return null;
    }

    try {
      const d = JSON.parse(el.textContent);
      // Kit sem peça com cor disponível não é oferta: é uma modal vazia na cara
      // de quem acabou de comprar. Melhor deixar a sacola abrir normal.
      const pecas = Array.isArray(d.pecas) ? d.pecas.filter((p) => p.opcoes && p.opcoes.length) : [];
      dados = pecas.length ? Object.assign({}, d, { pecas }) : false;
    } catch (e) {
      console.error('[conso-brinde-kit] JSON inválido na section', e);
      dados = false;
    }

    return dados || null;
  }

  const ehGatilho = (d, produtoId) => Array.isArray(d.gatilhos) && d.gatilhos.indexOf(Number(produtoId)) !== -1;

  /* --------------------------------------------------------------- carrinho */

  const idsDeSecaoDoCarrinho = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

  /** A resposta de erro do /cart/add muda de forma conforme o pedido. */
  const mensagemDeErro = (resposta) => {
    const errors = resposta && resposta.errors;

    if (typeof errors === 'string' && errors.trim()) return errors;

    if (errors && typeof errors === 'object') {
      const primeira = Object.values(errors)
        .flat()
        .find((m) => typeof m === 'string' && m.trim());
      if (primeira) return primeira;
    }

    return (resposta && (resposta.description || resposta.message)) || 'Não foi possível adicionar o kit.';
  };

  async function postCarrinho(itens) {
    const corpo = { items: itens };
    const secoes = idsDeSecaoDoCarrinho();
    if (secoes.length) {
      corpo.sections = secoes.join(',');
      corpo.sections_url = window.location.pathname;
    }

    const r = await fetch(rotas().cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(corpo),
    });
    const resposta = await r.json();
    if (!r.ok || resposta.status) throw new Error(mensagemDeErro(resposta));
    return resposta;
  }

  async function carrinhoAtual() {
    const r = await fetch(`${rotas().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  /**
   * Re-renderiza a sacola. Dispara o evento LEGADO de propósito: quem converte
   * pro formato da 4.x é o `conso-cart-event-bridge.js`, e ele usa
   * `action: 'update'` — que é justamente o que impede o auto-open por baixo da
   * modal.
   */
  function avisarTema(carrinho, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: carrinho,
          sourceId: 'conso-brinde-kit-atc',
          data: {
            itemCount: Number((carrinho && carrinho.item_count) || 0),
            source: 'conso-brinde-kit-atc',
            sections,
          },
        },
      })
    );
  }

  function abrirSacola() {
    liberar();
    // Chamada a partir do evento `close` do <dialog>: `modal.open` ja e false
    // aqui, entao a guarda acima nao barra esta abertura — que e a legitima.

    const drawer = document.querySelector('cart-drawer-component');
    const theme = drawer && drawer.closest('theme-drawer');
    if (theme && typeof theme.open === 'function') {
      theme.open();
      return;
    }
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    const gatilho = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (gatilho) gatilho.click();
  }

  function rastrear(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  /* ------------------------------------------------------------------ modal */

  function montarModal(d) {
    if (modal) return modal;

    modal = document.createElement('dialog');
    modal.className = 'conso-brinde conso-brinde-kit';
    modal.setAttribute('aria-labelledby', 'conso-brinde-kit-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-brinde__caixa';

    const cabecalho = document.createElement('div');
    cabecalho.className = 'conso-brinde__cabecalho';

    const titulo = document.createElement('h2');
    titulo.className = 'conso-brinde__titulo';
    titulo.id = 'conso-brinde-kit-titulo';
    titulo.textContent = d.modalTitulo || 'Seu kit de brinde está liberado';
    cabecalho.appendChild(titulo);

    if (d.modalSub) {
      const sub = document.createElement('p');
      sub.className = 'conso-brinde__sub';
      sub.textContent = d.modalSub;
      cabecalho.appendChild(sub);
    }

    const fechar = document.createElement('button');
    fechar.type = 'button';
    fechar.className = 'conso-brinde__fechar';
    fechar.setAttribute('aria-label', 'Fechar e ir para a sacola');
    fechar.innerHTML =
      '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
    fechar.addEventListener('click', () => pedirConfirmacao(d, 'x'));
    cabecalho.appendChild(fechar);

    const corpo = document.createElement('div');
    corpo.className = 'conso-brinde__corpo';
    corpo.appendChild(montarCard(d));

    caixa.append(cabecalho, corpo);
    modal.appendChild(caixa);

    // O <dialog> ocupa a tela toda e a caixa é filha: clique fora dela é no
    // backdrop. Não fecha direto — pergunta antes.
    modal.addEventListener('click', (e) => {
      if (e.target !== modal) return;
      pedirConfirmacao(d, 'backdrop');
    });

    // ESC: o <dialog> fecharia sozinho. `cancel` é o único gancho que segura
    // isso — sem o preventDefault a modal sumiria sem perguntar nada.
    modal.addEventListener('cancel', (e) => {
      e.preventDefault();
      pedirConfirmacao(d, 'esc');
    });

    // A modal só fecha de verdade em dois caminhos: kit adicionado ou recusa
    // confirmada. Os dois levam à sacola.
    modal.addEventListener('close', () => {
      if (!aceitou) rastrear({ event: 'conso_kit_brinde_recusa', kit_via: via });
      abrirSacola();
    });

    document.body.appendChild(modal);
    return modal;
  }

  /** O card do kit: uma linha por peça, cada uma com seu select de cor. */
  function montarCard(d) {
    const card = document.createElement('div');
    card.className = 'conso-brinde-kit-card';

    escolhas = d.pecas.map((peca) => peca.opcoes[0]);

    const lista = document.createElement('ul');
    lista.className = 'conso-brinde-kit-card__pecas';

    d.pecas.forEach((peca, i) => {
      const item = document.createElement('li');
      item.className = 'conso-brinde-kit-peca';

      const figura = document.createElement('div');
      figura.className = 'conso-brinde-kit-peca__figura';
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.src = escolhas[i].thumb || peca.imagem || '';
      figura.appendChild(img);

      const texto = document.createElement('div');
      texto.className = 'conso-brinde-kit-peca__texto';

      if (peca.rotulo) {
        const rot = document.createElement('p');
        rot.className = 'conso-brinde-kit-peca__rotulo';
        rot.textContent = peca.rotulo;
        texto.appendChild(rot);
      }

      const nome = document.createElement('p');
      nome.className = 'conso-brinde-kit-peca__nome';
      nome.textContent = peca.titulo;
      texto.appendChild(nome);

      const precos = document.createElement('p');
      precos.className = 'conso-brinde-kit-peca__precos';
      const riscado = document.createElement('s');
      riscado.textContent = peca.de;
      const gratis = document.createElement('strong');
      gratis.textContent = d.gratis || 'Grátis';
      precos.append(riscado, gratis);
      texto.appendChild(precos);

      const campo = document.createElement('div');
      campo.className = 'conso-brinde-kit-peca__campo';

      const idSelect = `conso-brinde-kit-cor-${i}`;
      const legenda = document.createElement('label');
      legenda.className = 'conso-brinde-kit-peca__legenda';
      legenda.setAttribute('for', idSelect);
      legenda.textContent = peca.rotuloCor || 'Cor';

      const select = document.createElement('select');
      select.className = 'conso-brinde-kit-peca__select';
      select.id = idSelect;
      peca.opcoes.forEach((op, j) => {
        const opt = document.createElement('option');
        opt.value = String(op.variantId);
        opt.textContent = op.nome;
        if (j === 0) opt.selected = true;
        select.appendChild(opt);
      });
      select.addEventListener('change', () => {
        const achada = peca.opcoes.find((op) => String(op.variantId) === select.value);
        if (!achada) return;
        escolhas[i] = achada;
        if (achada.thumb) img.src = achada.thumb;
      });

      campo.append(legenda, select);
      texto.appendChild(campo);

      item.append(figura, texto);
      lista.appendChild(item);
    });

    const erro = document.createElement('p');
    erro.className = 'conso-brinde-kit-card__erro';
    erro.setAttribute('role', 'alert');
    erro.hidden = true;

    const botao = document.createElement('button');
    botao.type = 'button';
    botao.className = 'conso-brinde-kit-card__botao';
    botao.innerHTML = `${d.iconeSacola || ''}<span>${d.textoBotao || 'Adicionar kit ao carrinho'}</span>`;
    botao.addEventListener('click', () => aceitar(d, botao, erro));

    card.append(lista, erro, botao);

    if (d.nota) {
      const nota = document.createElement('p');
      nota.className = 'conso-brinde-kit-card__nota';
      nota.textContent = d.nota;
      card.appendChild(nota);
    }

    const recusar = document.createElement('button');
    recusar.type = 'button';
    recusar.className = 'conso-brinde-kit-card__recusar';
    recusar.textContent = d.linkRecusar || 'Não quero o brinde';
    recusar.addEventListener('click', () => pedirConfirmacao(d, 'link'));
    card.appendChild(recusar);

    return card;
  }

  async function aceitar(d, botao, erro) {
    if (botao.disabled) return;
    botao.disabled = true;
    erro.hidden = true;

    const itens = escolhas.map((op) => ({
      id: op.variantId,
      quantity: 1,
      properties: { [PROP_BRINDE]: '1' },
    }));

    try {
      const resposta = await postCarrinho(itens);
      const carrinho = await carrinhoAtual();
      avisarTema(carrinho, resposta.sections);

      aceitou = true;
      rastrear({
        event: 'conso_kit_brinde_aceite',
        kit_pecas: escolhas.map((op, i) => `${d.pecas[i].titulo} / ${op.nome}`).join(' + '),
      });

      modal.close();
    } catch (e) {
      erro.textContent = e && e.message ? e.message : 'Não foi possível adicionar o kit.';
      erro.hidden = false;
      botao.disabled = false;
    }
  }

  /* ----------------------------------------------------------- confirmação */

  /**
   * Um segundo <dialog> em cima do primeiro, e não uma troca de conteúdo: assim
   * as cores que o cliente já escolheu continuam intactas atrás, e "voltar" é só
   * fechar esta.
   */
  function montarConfirmacao(d) {
    if (confirma) return confirma;

    confirma = document.createElement('dialog');
    confirma.className = 'conso-brinde-confirma';
    confirma.setAttribute('aria-labelledby', 'conso-brinde-kit-confirma-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-brinde-confirma__caixa';

    const titulo = document.createElement('h2');
    titulo.className = 'conso-brinde-confirma__titulo';
    titulo.id = 'conso-brinde-kit-confirma-titulo';
    titulo.textContent = d.confirmaTitulo || 'Tem certeza que não quer o kit?';
    caixa.appendChild(titulo);

    if (d.confirmaTexto) {
      const texto = document.createElement('p');
      texto.className = 'conso-brinde-confirma__texto';
      texto.textContent = d.confirmaTexto;
      caixa.appendChild(texto);
    }

    const acoes = document.createElement('div');
    acoes.className = 'conso-brinde-confirma__acoes';

    const voltar = document.createElement('button');
    voltar.type = 'button';
    voltar.className = 'conso-brinde-confirma__voltar';
    voltar.textContent = d.confirmaVoltar || 'Voltar e escolher o kit';
    voltar.addEventListener('click', () => confirma.close());

    const recusar = document.createElement('button');
    recusar.type = 'button';
    recusar.className = 'conso-brinde-confirma__recusar';
    recusar.textContent = d.confirmaRecusar || 'Sim, abrir mão do brinde';
    recusar.addEventListener('click', () => {
      confirma.close();
      // Um tick depois: o <dialog> de cima ainda está soltando o scroll-lock, e
      // fechar os dois no mesmo frame deixa o body travado.
      requestAnimationFrame(() => modal && modal.close());
    });

    acoes.append(voltar, recusar);
    caixa.appendChild(acoes);
    confirma.appendChild(caixa);

    // Esc/backdrop na confirmação só volta pra oferta — nunca desiste por ela.
    confirma.addEventListener('click', (e) => {
      if (e.target === confirma) confirma.close();
    });

    document.body.appendChild(confirma);
    return confirma;
  }

  function pedirConfirmacao(d, origem) {
    via = origem;
    const dlg = montarConfirmacao(d);
    if (!dlg.open) dlg.showModal();
  }

  /* ------------------------------------------------------------- disparador */

  function abrirModal(d) {
    const dlg = montarModal(d);
    if (!dlg.open) dlg.showModal();
    rastrear({ event: 'conso_kit_brinde_view' });
    // `setTimeout` e não `liberar()` direto: o cart-drawer da 4.x tenta abrir a
    // sacola de novo no `.then` da promise do evento, que é microtask. Soltar a
    // guarda no mesmo tick deixaria a sacola subir por baixo da modal.
    setTimeout(liberar, 0);
  }

  /* Arma no clique do add-to-cart de um produto-gatilho. */
  document.addEventListener(
    'click',
    (e) => {
      const alvo = e.target instanceof Element ? e.target : null;
      if (!alvo || !alvo.closest(SEL_ATC)) return;

      const form = alvo.closest(SEL_FORM);
      if (!form) return;

      const d = lerDados();
      if (!d || !ehGatilho(d, form.dataset.productId)) return;

      pendente = true;
      segurando = true;
      clearTimeout(expiracao);
      expiracao = setTimeout(desarmar, TETO_ESPERA_MS);
    },
    true
  );

  document.addEventListener(EVENTO_ADD, (evento) => {
    if (!pendente) return;
    pendente = false;
    clearTimeout(expiracao);

    const d = lerDados();
    if (!d) {
      liberar();
      return;
    }

    // Só o add de verdade. O evento também viaja em updates de quantidade.
    if (evento && evento.action && evento.action !== 'add') {
      liberar();
      return;
    }

    // Carrinho que já tem o brinde não recebe a oferta de novo: quem adicionou
    // um segundo par do gatilho não pode acumular kit.
    carrinhoAtual()
      .then((carrinho) => {
        const jaTem =
          carrinho &&
          Array.isArray(carrinho.items) &&
          carrinho.items.some((item) => item.properties && item.properties[PROP_BRINDE]);

        if (jaTem) {
          liberar();
          abrirSacola();
          return;
        }

        abrirModal(d);
      })
      .catch(() => {
        liberar();
        abrirSacola();
      });
  });
})();
